import clsx from 'clsx';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { Modal as RSModal } from 'reactstrap';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { useOnSaleEventsContext } from 'src/contexts/OnSaleEventsContext/OnSaleEventsContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { vars } from 'src/core/themes';
import { Button, Radio, RadioGroup, Stack } from 'src/core/ui';
import { FormatOption } from 'src/modals/EditTableColumns';
import { UnlockedNewIcon } from 'src/svgs';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { EventDiscoveryQuotaConfig } from 'src/WebApiController';

import * as styles from './OnSaleEventMetrics.css';

export type UnlockMetricsDialogProps = ComponentProps<typeof RSModal> & {
  isLoading?: boolean;
  onOkay: (quotaConfig: EventDiscoveryQuotaConfig) => void;
  onCancel: () => void;
};

export function UnlockMetricsDialog({
  isLoading,
  onOkay,
  onCancel,
  ...rest
}: UnlockMetricsDialogProps) {
  const [quotaConfig, updateQuotaConfig] =
    useState<EventDiscoveryQuotaConfig>();

  const { isDarkMode } = useSiteTheme();

  const { onSaleEventAccess } = useOnSaleEventsContext();

  const isUpgradePlan = useMemo(
    () =>
      onSaleEventAccess?.eventDiscoveryQuota?.eventViewQuota != null &&
      !onSaleEventAccess?.noProceedsAdvantage,
    [
      onSaleEventAccess?.eventDiscoveryQuota?.eventViewQuota,
      onSaleEventAccess?.noProceedsAdvantage,
    ]
  );

  const onResetTier = useCallback(() => {
    const { quotaConfigs } = onSaleEventAccess?.eventDiscoveryConfig ?? {};
    const availableOptions = (quotaConfigs ?? [])
      .filter(
        (config) =>
          onSaleEventAccess?.eventDiscoveryQuota?.eventViewQuotaUsed == null ||
          (config.eventViewQuota !==
            onSaleEventAccess.eventDiscoveryQuota.eventViewQuota &&
            config.eventViewQuota >
              onSaleEventAccess.eventDiscoveryQuota.eventViewQuotaUsed)
      )
      .toSorted((config) => config.eventViewQuota);

    updateQuotaConfig(availableOptions?.[0]);
  }, [
    onSaleEventAccess?.eventDiscoveryConfig,
    onSaleEventAccess?.eventDiscoveryQuota?.eventViewQuota,
    onSaleEventAccess?.eventDiscoveryQuota?.eventViewQuotaUsed,
  ]);

  const availableTiers = useMemo(() => {
    const { eventDiscoveryConfig, eventDiscoveryQuota } =
      onSaleEventAccess ?? {};
    if (!isUpgradePlan) {
      return eventDiscoveryConfig?.quotaConfigs ?? [];
    }
    return (eventDiscoveryConfig?.quotaConfigs ?? []).filter(
      ({ eventViewQuota }) =>
        eventViewQuota >= eventDiscoveryQuota!.eventViewQuota
    );
  }, [isUpgradePlan, onSaleEventAccess]);

  const currentText = useContent(ContentId.Current);
  const unlockDataText = useContent(ContentId.UnlockStubHubDemandData);
  const selectAccessText = useContent(ContentId.SelectYourAccess);
  const upgradeAccessText = useContent(ContentId.UpgradeAccess);
  const upgradeAccessDescriptionText = useContent(
    ContentId.UpgradeAccessDescription
  );
  const atHighestLevelText = useContent(ContentId.AtHighestLevel);
  const contactUsText = useContent(ContentId.ContactUsForMore);
  const confirmText = useContent(ContentId.Confirm);

  const titleText = useMemo(
    () => (isUpgradePlan ? upgradeAccessText : unlockDataText),
    [isUpgradePlan, unlockDataText, upgradeAccessText]
  );
  const subtitleText = useMemo(() => {
    if (availableTiers.length <= 1) {
      return atHighestLevelText;
    }
    if (isUpgradePlan) {
      return upgradeAccessDescriptionText;
    }
    return selectAccessText;
  }, [
    atHighestLevelText,
    availableTiers.length,
    isUpgradePlan,
    selectAccessText,
    upgradeAccessDescriptionText,
  ]);
  const actionButtonText = useMemo(() => {
    if (availableTiers.length <= 1) {
      return contactUsText;
    }
    return confirmText;
  }, [availableTiers.length, confirmText, contactUsText]);

  const curretnPlanPrefix = useMemo(() => {
    return `(${currentText})`;
  }, [currentText]);

  return (
    <GenericDialog
      {...rest}
      size="m"
      contentClassName={clsx(styles.OnSaleEventMetricsModalContainer, {
        [styles.OnSaleEventMetricsContainerDark]: isDarkMode,
      })}
      header={
        <Stack
          width="full"
          alignItems="center"
          gap="m"
          style={{ fontSize: vars.typography.fontSize.base }}
        >
          <div className={styles.OnSaleEventMetricsModalHeaderIcon}>
            <UnlockedNewIcon />
          </div>
          {titleText}
        </Stack>
      }
      onOpened={onResetTier}
      onClick={(e) => {
        // This so that this dialog does not cause event to flow to the EventHeader open/close event
        e.stopPropagation();
        e.preventDefault();
      }}
      footer={
        <Stack
          width="full"
          justifyContent="spaceBetween"
          alignItems="center"
          style={{
            paddingRight: vars.spacing.m,
            paddingBottom: vars.spacing.m,
          }}
        >
          <Button
            variant="textPlain"
            onClick={() => {
              onCancel();
            }}
          >
            <Content id={ContentId.Cancel} />
          </Button>

          <Button
            className={clsx({
              [styles.actionableDark]: isDarkMode,
            })}
            disabled={quotaConfig == null}
            onClick={() =>
              quotaConfig && availableTiers.length > 1 && onOkay(quotaConfig)
            }
          >
            {actionButtonText}
          </Button>
        </Stack>
      }
      onCancel={isLoading ? undefined : onCancel}
    >
      <Stack direction="column" gap="m" width="full">
        <Stack direction="column" gap="s" width="full">
          <div
            className={clsx({
              [styles.textColorDark]: isDarkMode,
            })}
          >
            {subtitleText}
          </div>
          {!isUpgradePlan && (
            <Stack direction="column" style={{ color: vars.color.textPrimary }}>
              <FormatContent
                id={FormatContentId.OnSaleEventAccessCreditRenewal}
                params={[
                  (onSaleEventAccess?.eventDiscoveryConfig
                    ?.proceedsAdvantageDurationInHours ?? 72) /
                    24 +
                    '',
                ]}
              />
            </Stack>
          )}
        </Stack>
        <Stack direction="column" gap="s" width="full">
          <RadioGroup
            onValueChange={(value: string) => {
              const selectedConfig = (
                onSaleEventAccess?.eventDiscoveryConfig?.quotaConfigs ?? []
              ).find(({ eventViewQuota }) => eventViewQuota + '' === value);
              if (
                selectedConfig &&
                selectedConfig.eventViewQuota !=
                  onSaleEventAccess?.eventDiscoveryQuota?.eventViewQuota
              ) {
                updateQuotaConfig(selectedConfig);
              }
            }}
            value={quotaConfig?.eventViewQuota + ''}
          >
            <Stack width="full" direction="column" gap="m">
              {availableTiers.map((config, index) => {
                const isCurrentPlan =
                  onSaleEventAccess?.eventDiscoveryQuota?.eventViewQuota !=
                    null &&
                  onSaleEventAccess?.eventDiscoveryQuota.eventViewQuota ===
                    config.eventViewQuota;
                return (
                  <Stack
                    key={index}
                    width="full"
                    className={clsx(styles.UnlockMetricsAccessContainer, {
                      [styles.UnlockMetricsAccessContainerSelected]:
                        quotaConfig?.eventViewQuota === config.eventViewQuota,
                      [styles.actionableDark]:
                        quotaConfig?.eventViewQuota === config.eventViewQuota &&
                        isDarkMode,
                      [styles.radioOptionDisabled]:
                        isCurrentPlan && isUpgradePlan,
                    })}
                    onClick={(e) => {
                      if (
                        onSaleEventAccess?.eventDiscoveryQuota
                          ?.eventViewQuota == null ||
                        config.eventViewQuota ===
                          onSaleEventAccess?.eventDiscoveryQuota?.eventViewQuota
                      ) {
                        return;
                      }
                      e?.preventDefault();
                      e?.stopPropagation();
                      updateQuotaConfig(config);
                    }}
                  >
                    <Radio value={config.eventViewQuota + ''} label={''} />
                    <Stack
                      width="full"
                      alignItems="center"
                      justifyContent="spaceBetween"
                      style={{ flex: 1 }}
                    >
                      <div>
                        <FormatContent
                          id={FormatContentId.EventViews}
                          params={[
                            applyNumberFormatting({
                              precision: FormatOption.Whole,
                              inputNumber: config.eventViewQuota,
                            }) as string,
                          ]}
                        />
                      </div>
                      <Stack direction="row" alignItems="center" gap="s">
                        {isCurrentPlan && <span>{curretnPlanPrefix}</span>}
                        <FormatContent
                          id={FormatContentId.OnSaleEventProceedsMarkup}
                          params={[
                            applyNumberFormatting({
                              isPercent: true,
                              inputNumber:
                                config.stubHubProceedsAdvantageAmount ?? 0,
                            }) as string,
                          ]}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </RadioGroup>
        </Stack>
      </Stack>
    </GenericDialog>
  );
}
